/** Tiny UI dictionary for the Studio (en / zh; other locales fall back to en). */
const en = {
    studio: "TongFlow Studio",
    noProjects: "no projects",
    newProject: "+ New project",
    openInSession: "Open in session",
    refresh: "refresh",
    pluginsKeys: "Plugins & keys",
    close: "close",
    createToStart: "Create a project to start.",
    noProjectYet: "No project yet.",
    createProject: "Create a project",
    engineNotReady: "engine not ready",
    selectHint: "Select a folder, workflow or file.",
    chatEmpty:
        "Describe what you want to make in the box below; the agent lays out the project folder and builds a workflow for every asset.",
    working: "● working…",
    save: "Save",
    cancel: "Cancel",
    open: "Open",
    json: "JSON",
    run: "▶ Run",
    runs: "Runs",
    runTitle: "Run · {name}",
    noInputs: "no inputs (static graph)",
    note: "note",
    notePlaceholder: "what changed / why",
    recentRuns: "Recent runs",
    plugins: "plugins",
    modality: "{modality} file — open in a new tab.",
    newProjectTitle: "New project",
    title: "Title",
    create: "Create",
    pluginsTitle: "TongFlow plugins & API keys",
    installed: "Installed",
    noneInstall: "none — install one below",
    remove: "Remove",
    removeConfirm: "Remove {id}?",
    install: "Install",
    officialPlugin: "official plugin…",
    orGitUrl: "or a git URL",
    apiKeys: "API keys / tokens",
    apiKeysHint:
        "Stored in the studio's env.json (0600) and passed to plugin processes. Keys already present in the host process environment or its config count as set.",
    set: "set",
    notSet: "not set",
    replace: "•••••• (replace)",
    pasteValue: "paste value",
    getKey: "get key",
    saveKeys: "Save keys",
    saving: "saving…",
    saved: "saved",
    saveFailed: "save failed",
    inputPlaceholder: "text · ./file.png · characters/mei/x.png · URL",
    required: "required",
    details: "Details",
    outputs: "Outputs",
    folderCount: "{folders} folders · {files} files",
    emptyFolder: "empty folder",
    brief: "Brief",
    titlePlaceholder: "My short film",
    briefPlaceholder:
        "What do you want to make? Genre, length, style, references, deliverables…",
    briefHint:
        "The agent reads this first and proposes a folder structure for it. You can change everything later.",
    billingTitle:
        "This run will use (first time in this project — please confirm):",
    billingApi: "paid API, billed to your key per call",
    billingModal:
        "Modal GPU, billed to your Modal account per second (first run deploys)",
    billingLocal: "local / free",
    model: "model",
    missingKeys: "missing API keys: {keys} — set them under Plugins & keys",
    confirmAndRun: "▶ Confirm & run",
    upload: "Upload",
    uploadHint:
        "Upload files into {dir}/ (select a folder in the tree to change the target)",
    uploading: "uploading…",
    uploaded: "{n} file(s) → {dir}/",
    uploadFailed: "upload failed",
    dropHere: "Drop files here to add them to this folder",
} as const;

export type UiKey = keyof typeof en;

const zh: Record<UiKey, string> = {
    studio: "TongFlow 工作室",
    noProjects: "还没有项目",
    newProject: "+ 新建项目",
    openInSession: "在会话中打开",
    refresh: "刷新",
    pluginsKeys: "插件与密钥",
    close: "关闭",
    createToStart: "先创建一个项目。",
    noProjectYet: "还没有项目。",
    createProject: "创建项目",
    engineNotReady: "引擎未就绪",
    selectHint: "在左侧选择文件夹、工作流或文件。",
    chatEmpty:
        "在下面说说你想做什么;Agent 会规划项目文件夹,并为每个素材建一个工作流。",
    working: "● 工作中…",
    save: "保存",
    cancel: "取消",
    open: "打开",
    json: "JSON",
    run: "▶ 运行",
    runs: "运行记录",
    runTitle: "运行 · {name}",
    noInputs: "无输入(静态图)",
    note: "备注",
    notePlaceholder: "改了什么 / 为什么",
    recentRuns: "最近运行",
    plugins: "插件",
    modality: "{modality} 文件 —— 在新标签页打开。",
    newProjectTitle: "新建项目",
    title: "标题",
    create: "创建",
    pluginsTitle: "TongFlow 插件与 API 密钥",
    installed: "已安装",
    noneInstall: "暂无 —— 在下方安装",
    remove: "移除",
    removeConfirm: "移除 {id}?",
    install: "安装",
    officialPlugin: "官方插件…",
    orGitUrl: "或 git 地址",
    apiKeys: "API 密钥 / 令牌",
    apiKeysHint:
        "保存在工作室的 env.json(0600),运行插件时注入。宿主进程环境或其配置里已有的密钥也算已设置。",
    set: "已设置",
    notSet: "未设置",
    replace: "•••••• (替换)",
    pasteValue: "粘贴值",
    getKey: "获取密钥",
    saveKeys: "保存密钥",
    saving: "保存中…",
    saved: "已保存",
    saveFailed: "保存失败",
    inputPlaceholder: "文本 · ./file.png · characters/mei/x.png · URL",
    required: "必填",
    details: "详情",
    outputs: "产物",
    folderCount: "{folders} 个文件夹 · {files} 个文件",
    emptyFolder: "空文件夹",
    brief: "需求",
    titlePlaceholder: "我的短片",
    briefPlaceholder: "想做什么?题材、时长、风格、参考、交付物……",
    briefHint: "Agent 会先读这段话,据此提出文件夹结构;之后随时可改。",
    billingTitle: "本次运行会产生费用,将用到:",
    billingApi: "付费 API,按次计费到你的 key",
    billingModal: "Modal GPU,按秒计费到你的 Modal 账户(首次运行需部署)",
    billingLocal: "本地 / 免费",
    model: "模型",
    missingKeys: "缺少 API 密钥:{keys} — 请到「插件与密钥」设置",
    confirmAndRun: "▶ 确认并运行",
    upload: "上传",
    uploadHint: "上传文件到 {dir}/(在树里选中文件夹可改目标)",
    uploading: "上传中…",
    uploaded: "{n} 个文件 → {dir}/",
    uploadFailed: "上传失败",
    dropHere: "把文件拖到这里,加入此文件夹",
};

const dicts: Record<string, Record<UiKey, string>> = { en, zh };

export type T = (key: UiKey, vars?: Record<string, string | number>) => string;

export function makeT(locale: string): T {
    const d = dicts[locale] ?? dicts[locale.split("-")[0]] ?? en;
    return (key, vars) => {
        let s: string = d[key] ?? en[key];
        for (const [k, v] of Object.entries(vars ?? {}))
            s = s.replaceAll(`{${k}}`, String(v));
        return s;
    };
}
